import { JSX, FC, useEffect, useState, useRef, MutableRefObject, useMemo } from "react";
import { getBasketCount, getMessengerCount } from "api/businessConnect";
import { handleError } from "utils/handleError";

// redux
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "redux/store/store";
import { AppStateType } from "redux/reducers/mainReducer";
import {
  fetchNotifications,
  fetchNotificationsCount,
  IFetchNotificationsParams
} from "redux/thunks/notifications.thunk";

// components
import NotificationsPanel from "./NotificationsPanel";
import { Badge } from "antd";
import { Link } from "react-router-dom";

// icons
import { ReactComponent as HelpIcon } from "assets/icons/help-icon.svg";
import { ReactComponent as NotificationsIcon } from "assets/icons/notifications-icon.svg";
import { ReactComponent as CartIcon } from "assets/icons/cart-icon.svg";
import { ReactComponent as MessagesIcon } from "assets/icons/messages-icon.svg";
import { ReactComponent as WalletIcon } from "assets/icons/wallet-icon.svg";

// style
import css from "./style.module.css";

// types
import { APP_PATHES } from "constants/appPathes";
import { NotificationsCountType } from "notifications/types";

interface IHeaderNavBarProps {
  isVendor: boolean;
}

const HeaderNavBar: FC<IHeaderNavBarProps> = ({ isVendor }): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();

  const { notifications: { count } } = useSelector((state: AppStateType) => state.notificationsSlice);

  const [cartCount, setCartCount] = useState<number | null>(null);
  const [messengerCount, setMessengerCount] = useState<number | null>(null);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState<boolean>(false);

  const timeoutRef: MutableRefObject<NodeJS.Timeout> = useRef<NodeJS.Timeout | null>(null);

  // Флаг для отслеживания первого рендера - лечит дублирование запросов
  const isFirstRender: MutableRefObject<boolean> = useRef<boolean>(true);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchDataCounts();

      getBasketCount()
        .then((res) => setCartCount(res.data?.count))
        .catch((err) => handleError("Не удалось получить информацию о корзине", err));
    }

    isFirstRender.current = false;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const oldCounts = useMemo((): Record<"oldBid" | "oldOrder" | "oldTotal", number> => {
    const { bid: oldBid, order: oldOrder, total: oldTotal } = count;

    return { oldBid, oldOrder, oldTotal };
  }, [count]);

  const fetchDataCounts = async (): Promise<void> => {
    const { oldBid, oldOrder, oldTotal } = oldCounts;

    dispatch(fetchNotificationsCount())
      .then(({ bid, order, total }: NotificationsCountType) => {
        let searchParams: IFetchNotificationsParams = { page: 1, page_size: 15, category: null };
        const needUpdate: boolean = !!oldTotal && (total > oldTotal);

        if (needUpdate) {
          const needBidUpdate: boolean = !!oldBid && (bid > oldBid);
          const needOrderUpdate: boolean = !!oldOrder && (order > oldOrder);

          if (needBidUpdate && needOrderUpdate) {
            searchParams = { ...searchParams, category: null };
          } else if (needBidUpdate) {
            searchParams = { ...searchParams, category: "bid" };
          } else if (needOrderUpdate) {
            searchParams = { ...searchParams, category: "order" };
          }

          dispatch(fetchNotifications(searchParams, needUpdate));
        }
      });

    await getMessengerCount()
      .then(({ data }) => {
        const newCount: number = data.reduce((total: number, item: { total: number }) => total + item.total, 0);

        setMessengerCount(newCount);
      })
      .catch((err) => handleError("Не удалось получить информацию о чате", err));

    timeoutRef.current = setTimeout(fetchDataCounts, 30000);
  };

  const renderBadge = (icon: JSX.Element, count: number = 0): JSX.Element => {
    return (
      <Badge
        count={count || null}
        size="small"
        style={{ top: 5, right: 5 }}
      >
        {icon}
      </Badge>
    );
  };

  const toggleNotifications = (): void => setIsNotificationsOpen(!isNotificationsOpen);

  return (
    <div className={`flex ${css.navBarBlock}`}>
      {!isVendor && (
        <Link to={APP_PATHES.HELP_MAIN}>
          <HelpIcon className={`cursor-pointer ${css.icon}`} />
        </Link>
      )}
      <div onClick={toggleNotifications}>
        {renderBadge(<NotificationsIcon className={`ml-4 cursor-pointer ${css.icon}`} />, count.total)}
      </div>
      {!isVendor && (
        <>
          {renderBadge(
            <Link to={isVendor ? "/platform/cart" : APP_PATHES.CART}>
              <CartIcon className={`ml-4 cursor-pointer ${css.icon}`} />
            </Link>,
            cartCount
          )}
          {renderBadge(
            <Link to={APP_PATHES.CHAT}>
              <MessagesIcon className={`ml-4 cursor-pointer ${css.icon}`} />
            </Link>,
            messengerCount
          )}
          <Link to={APP_PATHES.WALLET}>
            <WalletIcon className={`mx-4 cursor-pointer ${css.icon}`} />
          </Link>
        </>
      )}
      <NotificationsPanel
        isOpen={isNotificationsOpen}
        onToggle={toggleNotifications}
      />
    </div>
  );
};

export default HeaderNavBar;
